/**
 * Position Diary thunks - F2 shared infra.
 *
 * Plain thunk factories: (args) => async (dispatch) => {...}.
 * sanitize* helpers coerce API responses to safe defaults so the SPA never
 * renders undefined where a string is required (response-contract rule).
 */
import type { AppDispatch } from '../index';
import { setPositionDiaryEnabled } from '../../components/layouts/navigationConfig';
import {
  setLoading as setPositionsLoading,
  setError as setPositionsError,
  setPositions,
  upsertPosition,
  removePosition,
  setSelectedDetail,
  setFeatureEnabled,
  setPaidTierRequired,
} from '../slices/positionsSlice';
import {
  setLoading as setAlertsLoading,
  setError as setAlertsError,
  setEventsForPosition,
  ackEvent,
} from '../slices/positionAlertsSlice';
import {
  setLoading as setStatsLoading,
  setError as setStatsError,
  setStats,
} from '../slices/positionStatsSlice';
import {
  getPositionDiaryFeatureStatus,
  getPositions,
  createPosition,
  getPosition,
  updatePosition,
  closePosition,
  archivePosition,
  deletePosition,
  updatePositionLevels,
  getPositionAlerts,
  ackPositionAlert,
  getPositionStats,
} from '../../services/api';
import type {
  Position,
  PositionAlertEvent,
  PositionStats,
  CreatePositionPayload,
  UpdatePositionPayload,
  UpdatePositionLevelsPayload,
  ClosePositionPayload,
  PositionListFilters,
} from '../../types/positions';

// ---------------------------------------------------------------------------
// Sanitizers (safe defaults; never return null for required string fields)
// ---------------------------------------------------------------------------

export const sanitizePosition = (raw: unknown): Position | null => {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Partial<Position>;
  if (!p.id || !p.ticker) return null;
  return {
    ...(p as Position),
    ticker: (p.ticker ?? '').toUpperCase(),
    status: p.status ?? 'open',
    direction: p.direction ?? 'long',
    asset_type: p.asset_type ?? 'equity',
    setup_tag: p.setup_tag ?? '',
    entry_date_manual: p.entry_date_manual ?? '',
    average_entry_price: Number(p.average_entry_price ?? 0),
    quantity: Number(p.quantity ?? 0),
    created_at: p.created_at ?? '',
    updated_at: p.updated_at ?? '',
  };
};

export const sanitizePositions = (raw: unknown): Position[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(sanitizePosition).filter((p): p is Position => p !== null);
};

export const sanitizeAlertEvent = (raw: unknown): PositionAlertEvent | null => {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Partial<PositionAlertEvent>;
  if (!e.id) return null;
  return {
    ...(e as PositionAlertEvent),
    event_key: e.event_key ?? '',
    alert_type: e.alert_type ?? '',
    severity: e.severity ?? 'info',
    fired_at: e.fired_at ?? '',
    payload_json: e.payload_json ?? { explanation: '' },
  };
};

export const sanitizeAlertEvents = (raw: unknown): PositionAlertEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(sanitizeAlertEvent)
    .filter((e): e is PositionAlertEvent => e !== null);
};

export const sanitizeStats = (raw: unknown): PositionStats | null => {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<PositionStats>;
  return {
    total_positions: Number(s.total_positions ?? 0),
    open_count: Number(s.open_count ?? 0),
    trimmed_count: Number(s.trimmed_count ?? 0),
    closed_win_count: Number(s.closed_win_count ?? 0),
    closed_loss_count: Number(s.closed_loss_count ?? 0),
    win_rate_pct: s.win_rate_pct ?? null,
    average_r_multiple: s.average_r_multiple ?? null,
    total_realized_pnl: s.total_realized_pnl ?? null,
    average_hold_days: s.average_hold_days ?? null,
    attention_required_count: Number(s.attention_required_count ?? 0),
  };
};

// ---------------------------------------------------------------------------
// Helper: detect 402 / 503 from Axios-like error shapes
// ---------------------------------------------------------------------------

interface HttpErrLike {
  response?: { status?: number; data?: { error?: string; feature_disabled?: boolean } };
  message?: string;
}

const asHttpErr = (err: unknown): HttpErrLike | null => {
  if (err && typeof err === 'object' && ('response' in err || 'message' in err)) {
    return err as HttpErrLike;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchPositionDiaryFeatureStatus =
  () => async (dispatch: AppDispatch) => {
    try {
      const res = await getPositionDiaryFeatureStatus();
      const enabled = Boolean(res?.enabled);
      dispatch(setFeatureEnabled(enabled));
      setPositionDiaryEnabled(enabled);
    } catch (err) {
      const h = asHttpErr(err);
      if (h?.response?.status === 503 && h.response.data?.feature_disabled) {
        dispatch(setFeatureEnabled(false));
        setPositionDiaryEnabled(false);
      } else {
        // On network error default to disabled (kill-switch closes)
        dispatch(setFeatureEnabled(false));
        setPositionDiaryEnabled(false);
      }
    }
  };

export const fetchPositionsThunk =
  (filters?: PositionListFilters) => async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      const res = await getPositions(filters);
      dispatch(
        setPositions({
          positions: sanitizePositions(res.positions),
          total: res.total ?? 0,
        })
      );
    } catch (err) {
      const h = asHttpErr(err);
      if (h?.response?.status === 402) {
        dispatch(setPaidTierRequired(true));
        dispatch(setPositionsLoading(false));
      } else if (h?.response?.status === 503 && h.response.data?.feature_disabled) {
        dispatch(setFeatureEnabled(false));
        setPositionDiaryEnabled(false);
        dispatch(setPositionsLoading(false));
      } else {
        const msg =
          h?.message ??
          (err instanceof Error ? err.message : 'Failed to load positions');
        dispatch(setPositionsError(msg));
      }
    }
  };

export const fetchPositionDetailThunk =
  (id: number) => async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      const res = await getPosition(id);
      dispatch(setSelectedDetail(res));
      if (res.position) {
        const sanitized = sanitizePosition(res.position);
        if (sanitized) dispatch(upsertPosition(sanitized));
      }
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to load position');
      dispatch(setPositionsError(msg));
    }
  };

export const createPositionThunk =
  (payload: CreatePositionPayload) => async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      const res = await createPosition(payload);
      if (res.position) {
        const sanitized = sanitizePosition(res.position);
        if (sanitized) dispatch(upsertPosition(sanitized));
      }
      dispatch(setPositionsLoading(false));
      return res;
    } catch (err) {
      const h = asHttpErr(err);
      if (h?.response?.status === 402) {
        dispatch(setPaidTierRequired(true));
        dispatch(setPositionsLoading(false));
      } else {
        const msg =
          h?.message ??
          (err instanceof Error ? err.message : 'Failed to create position');
        dispatch(setPositionsError(msg));
      }
      throw err;
    }
  };

export const updatePositionThunk =
  (id: number, payload: UpdatePositionPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      const res = await updatePosition(id, payload);
      if (res.position) {
        const sanitized = sanitizePosition(res.position);
        if (sanitized) dispatch(upsertPosition(sanitized));
      }
      dispatch(setSelectedDetail(res));
      dispatch(setPositionsLoading(false));
      return res;
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to update position');
      dispatch(setPositionsError(msg));
      throw err;
    }
  };

export const updatePositionLevelsThunk =
  (id: number, payload: UpdatePositionLevelsPayload) =>
  async (dispatch: AppDispatch) => {
    try {
      const res = await updatePositionLevels(id, payload);
      // Refresh the full detail to get recomputed derived metrics
      await dispatch(fetchPositionDetailThunk(id));
      return res;
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to update levels');
      dispatch(setPositionsError(msg));
      throw err;
    }
  };

export const closePositionThunk =
  (id: number, payload: ClosePositionPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      await closePosition(id, payload);
      // Refetch full detail so selectedDetail always contains the complete
      // nested shape (derived, levels, thesis_health, exit_signal) rather
      // than whatever partial envelope the /close endpoint may return.
      await dispatch(fetchPositionDetailThunk(id));
      dispatch(setPositionsLoading(false));
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to close position');
      dispatch(setPositionsError(msg));
      throw err;
    }
  };

export const archivePositionThunk =
  (id: number) => async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      await archivePosition(id);
      // Remove from the local list rather than upsert-ing a skeleton {id}
      // object (which would corrupt the row in the Redux store by overwriting
      // every required field with undefined).
      dispatch(removePosition(id));
      dispatch(setPositionsLoading(false));
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to archive position');
      dispatch(setPositionsError(msg));
      throw err;
    }
  };

export const deletePositionThunk =
  (id: number, confirmTicker?: string) => async (dispatch: AppDispatch) => {
    dispatch(setPositionsLoading(true));
    try {
      await deletePosition(id, confirmTicker);
      dispatch(removePosition(id));
      dispatch(setPositionsLoading(false));
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to delete position');
      dispatch(setPositionsError(msg));
      throw err;
    }
  };

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export const fetchPositionAlertsThunk =
  (positionId: number) => async (dispatch: AppDispatch) => {
    dispatch(setAlertsLoading(true));
    try {
      const res = await getPositionAlerts(positionId);
      dispatch(
        setEventsForPosition({
          positionId,
          events: sanitizeAlertEvents(res.events),
        })
      );
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to load alerts');
      dispatch(setAlertsError(msg));
    }
  };

export const ackPositionAlertThunk =
  (positionId: number, alertId: number) => async (dispatch: AppDispatch) => {
    try {
      await ackPositionAlert(positionId, alertId);
      dispatch(
        ackEvent({ positionId, alertId, at: new Date().toISOString() })
      );
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to ack alert');
      dispatch(setAlertsError(msg));
    }
  };

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export const fetchPositionStatsThunk =
  () => async (dispatch: AppDispatch) => {
    dispatch(setStatsLoading(true));
    try {
      const res = await getPositionStats();
      const sanitized = sanitizeStats(res.stats);
      if (sanitized) dispatch(setStats(sanitized));
      else dispatch(setStatsLoading(false));
    } catch (err) {
      const h = asHttpErr(err);
      const msg =
        h?.message ??
        (err instanceof Error ? err.message : 'Failed to load stats');
      dispatch(setStatsError(msg));
    }
  };
